import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { classifiedErrorMessages } from 'config/apiErrors';
import ErrorPopup from 'reusable/ErrorPopup';

/**
 * 保存・削除に失敗したときの説明を表示する。
 *
 * 画面ごとに失敗をコンソールへ出すだけだったため、利用者には理由が分からな
 * かった。catch で showSaveError(error) を呼び、saveErrorPopup を描画する。
 * 表示はダイアログより前面に出る（ErrorPopup の z-50）。ダイアログを閉じずに呼べるので、
 * 失敗したときに入力を残したまま理由を見せられる（ADM-15 / ADM-24）。
 */
/**
 * ADM-15-AC1。失敗の種類（通信断・403・5xx・入力の誤り）を区別した説明の文を返す関数。
 * ダイアログの中に説明を出す画面（作業の追加・ドライバー割当・運転者の新規作成）で使う。
 */
export function useClassifiedErrorMessages() {
  const { t } = useTranslation('common');
  return useCallback(
    (error: unknown, fallback?: string) =>
      classifiedErrorMessages(error, {
        network: t('ui.error-network', { ns: 'common' }),
        forbidden: t('ui.error-forbidden', { ns: 'common' }),
        server: t('ui.error-server', { ns: 'common' }),
        fallback: fallback ?? t('ui.save-failed', { ns: 'common' }),
      }),
    [t]
  );
}

export function useSaveError() {
  const { t } = useTranslation('common');
  const [messages, setMessages] = useState<string[]>([]);
  const errorMessages = useClassifiedErrorMessages();

  const showSaveError = useCallback(
    (error: unknown, fallback?: string) => setMessages(errorMessages(error, fallback)),
    [errorMessages]
  );

  /** 入力の確認など、サーバーを介さない説明を同じ表示で出す。 */
  const showMessages = useCallback((list: string[]) => setMessages(list), []);

  const saveErrorPopup =
    messages.length > 0 ? (
      <ErrorPopup title={t('ui.error')} messages={messages} onClose={() => setMessages([])} />
    ) : null;

  return { showSaveError, showMessages, saveErrorPopup };
}
